import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import {
  getDoc,
  setDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  addDoc,
  collection,
} from "firebase/firestore";

import { auth, db } from "../firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubRole = () => {};

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);

      // Signed out
      if (!u) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const userRef = doc(db, "users", u.uid);
      const adminRef = doc(db, "admins", u.uid);

      try {
        // Ensure user doc exists
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            displayName: u.displayName || u.email,
            email: u.email,
            credits: 0,
            createdAt: serverTimestamp(),
          });
        }

        // Log login activity
        await logUserActivity(u.uid, "Logged In", `User logged in with email ${u.email}`);

        // Manual check for admin role
        const adminSnap = await getDoc(adminRef);
        setIsAdmin(adminSnap.exists());

        // Real-time admin role sync
        unsubRole = onSnapshot(
          adminRef,
          (snap) => {
            setIsAdmin(snap.exists());
            setLoading(false);
          },
          (err) => {
            console.error("🔥 Failed to listen for admin role:", err);
            setIsAdmin(false);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("🔥 Auth context error:", error);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      unsubRole();
    };
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = async () => {
    try {
      if (auth.currentUser) {
        await logUserActivity(auth.currentUser.uid, "Logged Out", "User logged out");
      }
      await signOut(auth);
    } catch (err) {
      console.error("Failed to log logout activity:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, isAdmin, loading, login, resetPassword, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ✅ Activity Logger
async function logUserActivity(uid, action, description = "") {
  if (!uid) return;
  try {
    await addDoc(collection(db, "users", uid, "activityLogs"), {
      action,
      description,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error("🔥 Failed to log user activity:", error);
  }
}
